import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { Mail, Lock, User, ArrowRight, Loader2, AlertCircle, CheckCircle, ShieldCheck, Monitor } from 'lucide-react';
import posthog from 'posthog-js';

const AUTH_PROXY_URL = "https://everydaycats-thrust-auth-server.hf.space"; // YOUR PROXY URL

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // CLI Context
  const localPort = searchParams.get('localPort');
  const isCLIAuthRequest = !!localPort;

  // UI States
  const [mode, setMode] = useState('login');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authProcessing, setAuthProcessing] = useState(false);

  // Form States
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for Auth Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      if (session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          full_name: session.user.user_metadata?.full_name
        });
        // If not CLI auth, go to console
        if (!isCLIAuthRequest) {
          const destination = location.state?.from || '/console';
          navigate(destination, { replace: true });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, location, isCLIAuthRequest]);

  // --- CLI AUTHORIZATION HANDLER ---
  const handleAuthorizeCLI = async () => {
    setAuthProcessing(true);
    setError(null);
    try {
      // 1. Get Temp Key from Proxy using Supabase Identity
      const keyRes = await fetch(`${AUTH_PROXY_URL}/key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ projectId: 'default' })
      });
      const keyData = await keyRes.json();
      if (!keyData.key) throw new Error(keyData.error || "Failed to generate key");

      // 2. Ping Local CLI with the Temp Key
     /* const localRes = await fetch(`http://localhost:${localPort}/api/auth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempKey: keyData.key })
      }); */
	    // 2. Ping Local CLI with the Temp Key AND Email
      const localRes = await fetch(`http://localhost:${localPort}/api/auth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
        tempKey: keyData.key, 
         email: session.user.email // <-- ADD THIS LINE
        })
      });

      if (localRes.ok) {
        setSuccessMsg("Success! Agent Linked.");
        setTimeout(() => window.close(), 2000);
      } else {
        throw new Error("CLI connection failed.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAuthProcessing(false);
    }
  };

  // --- STANDARD AUTH HANDLERS ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthProcessing(true);
    setError(null);
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({
          email, password, options: { data: { full_name: fullName } }
        });
        if (error) throw error;
        setSuccessMsg("Verification email sent!");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAuthProcessing(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#06060a] flex items-center justify-center"><Loader2 className="animate-spin text-purple-400" size={40} /></div>;

  // Updated styling to purple / dark theme (Thrust-like)
  const inputClass = "w-full bg-[#0f1114] border border-[#25262a] rounded-lg pl-10 pr-4 py-3.5 text-slate-100 focus:ring-2 focus:ring-purple-600 outline-none transition-all placeholder:text-slate-600";
  const labelClass = "block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1";

  return (
    <div className="min-h-screen bg-[#06060a] flex items-center justify-center p-6 font-sans text-slate-200">
      {/* soft purple glow behind card */}
      <div className="fixed top-[-10%] left-[-5%] w-[600px] h-[600px] bg-purple-600/8 rounded-full blur-[128px] pointer-events-none" />

      {/* Spacious Container (max-w-lg) */}
      <div className="w-full max-w-lg relative z-10 animate-in fade-in zoom-in duration-500">
        <div className="bg-[#0b0c0e]/80 backdrop-blur-xl border border-[#1f1f23]/50 rounded-3xl shadow-2xl overflow-hidden">
          {/* purple gradient top accent */}
          <div className="h-1.5 w-full bg-gradient-to-r from-purple-600 to-fuchsia-600" />

          <div className="p-12">
            {/* STATE 1: AUTHORIZE VIEW (Logged in + CLI Request) */}
            {isCLIAuthRequest && session && !successMsg ? (
              <div className="text-center">
                <div className="flex justify-center gap-4 mb-8">
                  <ShieldCheck className="text-purple-400" size={48} />
                  <div className="h-12 w-px bg-[#161618]" />
                  <Monitor className="text-fuchsia-500" size={48} />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">Authorize Agent</h1>
                <p className="text-slate-400 mb-10 text-lg">Grant local CLI access to <span className="text-white font-bold">{session.user.email}</span>?</p>
                {error && <div className="bg-red-900/20 border border-red-500/50 p-4 rounded-xl text-red-400 mb-6 text-sm">{error}</div>}
                <button
                  onClick={handleAuthorizeCLI}
                  disabled={authProcessing}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 text-xl transition-all"
                >
                  {authProcessing ? <Loader2 className="animate-spin text-white" /> : "Authorize Access"}
                </button>
                <button onClick={() => window.close()} className="mt-4 text-slate-500 hover:text-white transition-colors">Cancel</button>
              </div>
            ) : successMsg ? (
              /* STATE 2: SUCCESS VIEW */
              <div className="text-center py-10">
                <CheckCircle className="mx-auto text-green-400 mb-6" size={64} />
                <h2 className="text-3xl font-bold text-white mb-2">{successMsg}</h2>
                <p className="text-slate-400">You may now close this window.</p>
              </div>
            ) : (
              /* STATE 3: LOGIN / SIGNUP FORM */
              <>
                <div className="text-center mb-10">
                  <h1 className="text-4xl font-extrabold text-white mb-3">
                    {mode === 'login' ? 'Welcome Back' : 'Create Account'}
                  </h1>
                  <p className="text-slate-400 text-lg">
                    {isCLIAuthRequest ? 'Sign in to authorize your agent.' : 'Access your Proactive AI Director.'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {mode === 'signup' && (
                    <div className="animate-in fade-in slide-in-from-top-4">
                      <label className={labelClass}>Full Name</label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-4 text-slate-500" size={18} />
                        <input type="text" required className={inputClass} placeholder="John Doe" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-4 text-slate-500" size={18} />
                      <input type="email" required className={inputClass} placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-4 text-slate-500" size={18} />
                      <input type="password" required className={inputClass} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                    </div>
                  </div>
                  {error && <div className="p-4 bg-red-950/30 border border-red-900/50 text-red-400 rounded-xl text-sm">{error}</div>}
                  <button type="submit" disabled={authProcessing} className="w-full py-4 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold rounded-xl text-xl flex items-center justify-center gap-2 transition-all active:scale-95">
                    {authProcessing ? <Loader2 className="animate-spin text-white" /> : (mode === 'login' ? 'Sign In' : 'Create Account')}
                  </button>
                </form>

                <div className="text-center mt-10">
                  <button onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} className="text-purple-400 font-bold hover:text-purple-300">
                    {mode === 'login' ? "New here? Create an account" : "Have an account? Sign in"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
